/**
 * Module dependencies.
 */

import JsonView from '@franciscomorais/react18-json-view';
import { Button } from 'app/components/atoms/button/button';
import { usePostRetryOrder } from 'app/hooks/app-requests/orders/use-post-retry-order';
import React, { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { ifNotProp } from 'styled-tools';

/**
 * `Container` styled component.
 */

const Container = styled.div`
  padding-top: 20px;
  color: #333;
`;

/**
 * `Section` component.
 */

const Section = styled.div`
  margin-bottom: 60px;
`;

/**
 * `Title` component.
 */

const Title = styled.h2`
  margin-bottom: 16px;
  color: #005b96;
`;

/**
 * `KeyValueRow` component.
 */

const KeyValueRow = styled.div<{ last?: boolean }>`
  display: flex;
  padding: 6px 0;

  ${ifNotProp(
    'last',
    css`
      border-bottom: 1px solid #eee;
    `
  ) as any};
`;

/**
 * `Key` component.
 */

const Key = styled.div`
  flex: 0 0 300px;
  font-weight: bold;
`;

/**
 * `Value` component.
 */

const Value = styled.div`
  flex: 1;
  word-break: break-word;
`;

/**
 * Render value.
 */

function renderValue(value: any) {
  if (value === null || value === undefined) {
    return '-';
  }

  // se for objeto traduzido { pt: "..." }
  if (typeof value === 'object' && value.pt) {
    return value.pt;
  }

  // se for array
  if (Array.isArray(value)) {
    return (
      <ul>
        {value.map((v, i) => (
          <li key={i}>{typeof v === 'object' ? JSON.stringify(v) : v}</li>
        ))}
      </ul>
    );
  }

  // se for objeto simples
  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2);
  }

  return String(value);
}

/**
 * Export `OrderDetail` component.
 */

export function OrderDetail(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const order: any = location.state.order as any;

  let reasonsHistory: any[] = [];
  const { mutateAsync: retryOrder } = usePostRetryOrder();
  try {
    if (order.metadata) {
      const parsed = JSON.parse(order.metadata);
      reasonsHistory = parsed.reasonsHistory || [];
    }
  } catch (err) {
    console.error('Erro a parsear metadata', err);
  }

  const handleChangeStatus = useCallback(async () => {
    await retryOrder({ id: order.id });
    navigate(-1);
  }, [navigate, order.id, retryOrder]);

  return (
    <Container>
      <Section>
        <Title>{'📌 Order'}</Title>

        {Object.entries(order).map(([key, value]) => {
          if (key === 'metadata' || key === 'user' || key === 'pharmacy' || key === 'lines') {
            return null;
          }

          return (
            <KeyValueRow key={key}>
              <Key>{key}</Key>

              <Value>{renderValue(value)}</Value>
            </KeyValueRow>
          );
        })}
      </Section>

      <Section>
        <Title>{'💊 Lines'}</Title>
        {order.lines?.map((line: any, idx: number) => (
          <div
            key={line.id || idx}
            style={{ marginBottom: '20px', padding: '10px', border: '1px solid #eee', borderRadius: '8px' }}
          >
            <h2>{`Item ${idx}`}</h2>

            <KeyValueRow>
              <Key>{'id'}</Key>

              <Value>{line.id}</Value>
            </KeyValueRow>

            <KeyValueRow>
              <Key>{'code'}</Key>

              <Value>{line.code}</Value>
            </KeyValueRow>

            <KeyValueRow>
              <Key>{'codeType'}</Key>

              <Value>{line.codeType}</Value>
            </KeyValueRow>

            <KeyValueRow>
              <Key>{'drugName'}</Key>

              <Value>{line.drug?.productName ? JSON.parse(line.drug.productName).pt : '-'}</Value>
            </KeyValueRow>

            <KeyValueRow>
              <Key>{'drugDescription'}</Key>

              <Value>{line.drug?.productDescription ? JSON.parse(line.drug.productDescription).pt : '-'}</Value>
            </KeyValueRow>

            <KeyValueRow>
              <Key>{'drugId'}</Key>

              <Value>{line.drug?.id ?? '-'}</Value>
            </KeyValueRow>

            <KeyValueRow last>
              <Key>{'drugRegistry'}</Key>

              <Value>{line.drug?.registry ?? '-'}</Value>
            </KeyValueRow>
          </div>
        ))}
      </Section>

      <Section>
        <Title>{'👤 User'}</Title>
        {order.user &&
          Object.entries(order.user).map(([k, v]) => (
            <KeyValueRow key={k}>
              <Key>{k}</Key>
              <Value>{renderValue(v)}</Value>
            </KeyValueRow>
          ))}
      </Section>

      {/* ---- Pharmacy ---- */}
      <Section>
        <Title>{'🏥 Pharmacy'}</Title>
        {order.pharmacy &&
          Object.entries(order.pharmacy).map(([k, v]) => (
            <KeyValueRow key={k}>
              <Key>{k}</Key>
              <Value>{renderValue(v)}</Value>
            </KeyValueRow>
          ))}
      </Section>

      {/* ---- Reasons History ---- */}
      <Section>
        <Title>{'⚠️ Reasons History'}</Title>
        {reasonsHistory.length === 0 ? (
          <p>{'Sem histórico'}</p>
        ) : (
          reasonsHistory.map((row, idx) => (
            <div
              key={idx}
              style={{ marginBottom: '20px', padding: '10px', border: '1px solid #eee', borderRadius: '8px' }}
            >
              {(Object.entries(row) as any).map((item: any) => {
                const [key, value] = item;

                if (key === 'orderData') {
                  return (
                    <div
                      key={key}
                      style={{ marginBottom: '20px', padding: '10px', border: '1px solid #eee', borderRadius: '8px' }}
                    >
                      <h4>{'📦 orderData'}</h4>

                      <JsonView collapsed src={value} theme={'vscode'} />
                    </div>
                  );
                }
                return (
                  <KeyValueRow key={key}>
                    <Key>{key}</Key>

                    <Value>{renderValue(value)}</Value>
                  </KeyValueRow>
                );
              })}
            </div>
          ))
        )}
      </Section>

      <Button onClick={handleChangeStatus} size={'large'} type={'primary'}>
        {'Change status to CREATED'}
      </Button>
    </Container>
  );
}
